import React, { useEffect, useState } from 'react';
import { TextField, Alert } from '@mui/material';
import Modal from '../../common/Modal';
import { useAlert } from '../../../contexts/AlertContext';
import { employmentApi } from '../../../services/api';
import { contractIdentityLabel } from '../../../constants/contractActions';
import { ContractNumberField } from './ContractTermsFields';
import {
  storedContractNumber,
  toContractNumberInput,
  contractNumberInputProblem,
  contractNumberRefusalMessage
} from '../../../utils/contractNumber';

/**
 * ADR-174 §5 — the Contract Number verb: set, correct or clear ONE contract's
 * number (payload.sourceReference.contractId — the same field as the Employment
 * Episodes tab's Source Contract ID, written through the same server merge).
 *
 * The title names the contract by its identity, so the operator sees WHICH of
 * the person's contracts they are numbering — the new failure mode concurrency
 * introduces is the right number on the wrong contract (§10).
 *
 * Save runs a dry run first: a number already used in the Region is shown
 * inline, before anything is written (§5.4 rule 5).
 */
const ContractNumberModal = ({ employee, episode, onClose, onCompleted }) => {
  const { showAlert } = useAlert();
  const current = storedContractNumber(episode);
  const [value, setValue] = useState(current || '');
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(current || '');
    setReason('');
    setRefusal(null);
  }, [episode?.id, current]);

  if (!episode) return null;

  const next = toContractNumberInput(value);
  const problem = contractNumberInputProblem(value);
  const unchanged = next === current;
  const clearing = current !== null && next === null;
  const identity = contractIdentityLabel(null, episode) || 'Contract';
  const name = employee?.fullName || 'Individual';

  const submit = async () => {
    setSaving(true);
    setRefusal(null);
    try {
      const body = { contractNumber: next, reason: reason.trim() || undefined };
      await employmentApi.setContractNumber(episode.id, body, { dryRun: true });
      await employmentApi.setContractNumber(episode.id, body);
      showAlert(
        clearing ? `Contract number cleared for ${name}` : `Contract number saved for ${name}`,
        'success'
      );
      await onCompleted();
    } catch (error) {
      const message = contractNumberRefusalMessage(error?.data) || error.message;
      if (error?.status === 409 || error?.status === 400) {
        setRefusal(message);
      } else {
        showAlert(message || 'Failed to save the contract number', 'error', error);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      onCancel={onClose}
      title={`${current ? 'Edit' : 'Add'} Contract Number — ${identity}`}
      maxWidth="sm"
      fullWidth
      submitText={clearing ? 'Clear Number' : 'Save'}
      onSubmit={submit}
      submitDisabled={saving || unchanged || Boolean(problem)}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
        <ContractNumberField
          value={value}
          onChange={(v) => { setValue(v); setRefusal(null); }}
          autoFocus
          error={Boolean(problem || refusal)}
          helperText={problem
            || 'Text, exactly as the contract shows it — leading zeros are kept. Unique within the Region. Leave empty to clear.'}
        />
        <TextField
          label="Reason (optional)"
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText="Added to the contract's comments. The change itself is recorded in the Employment Change Log."
        />
        {clearing && (
          <Alert severity="info">
            Clearing removes number {current} from this contract. To move a number to the right contract,
            clear it here first, then add it there.
          </Alert>
        )}
        {refusal && <Alert severity="error">{refusal}</Alert>}
      </div>
    </Modal>
  );
};

export default ContractNumberModal;
